"use client";
import { useRef, useEffect, useState, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import type { HighchartsReactRefObject } from "highcharts-react-official";

type MarketCapData = {
  label: string;
  value: number;
};

type MarketCapChartProps = {
  data: MarketCapData[];
  totalValue?: number;
  chartName?: string;
  className?: string;
  tabs?: { label: string; value: string; active: boolean }[];
};

type CustomChart = Highcharts.Chart & {
  _customLabel?: Highcharts.SVGElement;
};

const PALETTE = [
  "#657DFF",
  "#5ED5A8",
  "#FBBF24",
  "#FF6B6B",
  "#FFD93D",
  "#6BCB77",
  "#4D96FF",
  "#9333EA",
  "#06B6D4",
  "#9CA3AF",
  "#D97706",
  "#F472B6",
];

export default function MarketCapChart({ data, totalValue, chartName, className }: MarketCapChartProps) {
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const [chartTitle, setChartTitle] = useState("");

  const coloredData = useMemo(() => {
    let i = 0;
    return data
      .filter((item) => item.value > 0)
      .map((item) => ({
        ...item,
        value: Number(item.value.toFixed(2)),
        color: PALETTE[i++ % PALETTE.length],
      }));
  }, [data]);

  useEffect(() => {
    if (chartName != undefined) {
      setChartTitle(chartName);
    }
  }, [chartName]);

  const pieSeries = {
    type: "pie",
    name: "Market Share",
    innerSize: "65%",
    colorByPoint: true,
    dataLabels: {
      enabled: false,
    },
    data: coloredData.map((item) => ({
      name: item.label,
      y: item.value,
      color: item.color,
    })),
  };

  const options: Highcharts.Options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: 190,
      spacing: [0, 0, 0, 0],
    },
    title: { text: undefined },
    tooltip: {
      pointFormat: "<b>{point.percentage:.2f}%</b>",
    },
    legend: { enabled: false },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        showInLegend: false,
      },
    },
    series: [pieSeries] as Highcharts.SeriesOptionsType[],
    credits: { enabled: false },
  };

  useEffect(() => {
    if (!totalValue || !chartTitle) return;

    const chart = chartRef.current?.chart as CustomChart;
    if (!chart) return;

    if (chart._customLabel) {
      chart._customLabel.destroy();
    }

    const centerX = chart.plotLeft + chart.plotWidth / 2;
    const centerY = chart.plotTop + chart.plotHeight / 2;

    const label = chart.renderer
      .label(
        `<div class="relative z-[-1] w-40 text-center">
        <span class="w-min inline-block font-semibold text-sm leading-4 text-primaryBlack">${chartTitle} Market Cap</span>
      </div>`,
        centerX - 40,
        centerY - 30,
        undefined,
        undefined,
        undefined,
        true,
      )
      .css({ width: 80 })
      .attr({ align: "center", zIndex: 10 })
      .add();

    // Save the label to chart instance so it can be destroyed on next update
    chart._customLabel = label;
  }, [totalValue, data, chartTitle]);

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-evenly gap-4 mb-5 sm:mb-0 ${className}`}>
      {/* Left Chart */}
      <div className="flex-1 mx-auto sm:mx-0 max-w-60 h-47.5">
        <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
      </div>

      {/* Right Legend */}
      <div className="flex-1 w-full sm:max-w-72">
        <div className="space-y-6">
          {coloredData.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-block w-3.5 h-3.5 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-base">{item.label}</span>
              </div>
              <div className="text-sm text-primaryBlack font-semibold">{(item.value || 0).toFixed(2)}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
